import api from '@/api/axiosApi'
import type { ExportRead } from '@/types'
import type { CreateExportResponse } from '@/hooks/useExport'

/** Most plan ids one SKU export may list (mirrors the backend cap) */
export const MAX_EXPORT_PLAN_IDS = 5000

export interface CreatePlanExportRequest {
  search?: string
  status?: string[]
  owner_ids?: string[]
  hierarchy_ids?: string[]
  season_ids?: string[]
  /** SKU export only: export exactly these plans instead of all filtered plans */
  plan_ids?: string[]
}

export interface CreateLineExportRequest {
  columns?: string[] | null
  sort?: string | null
  workflow_status?: string | null
  sku_number?: string | null
  vendor_id?: string | null
}

export type { CreateExportResponse }

/**
 * Create a new plan export job
 * POST /plans/exports
 */
export async function createPlanExport(
  request?: CreatePlanExportRequest
): Promise<CreateExportResponse> {
  const response = await api.post<CreateExportResponse>(
    '/plans/exports',
    request ?? {}
  )
  return response.data
}

/**
 * Get export status and download URL
 * GET /plans/exports/{exportId}
 */
export async function getPlanExport(exportId: string): Promise<ExportRead> {
  const response = await api.get<ExportRead>(`/plans/exports/${exportId}`)
  return response.data
}

/**
 * Create a plan-details export job
 * POST /plans/plan-details-exports
 */
export async function createPlanDetailsExport(
  request?: CreatePlanExportRequest
): Promise<CreateExportResponse> {
  const response = await api.post<CreateExportResponse>(
    '/plans/plan-details-exports',
    request ?? {}
  )
  return response.data
}

/**
 * Get plan-details export status and download URL
 * GET /plans/plan-details-exports/{exportId}
 */
export async function getPlanDetailsExport(
  exportId: string
): Promise<ExportRead> {
  const response = await api.get<ExportRead>(
    `/plans/plan-details-exports/${exportId}`
  )
  return response.data
}

/**
 * Create a new plan lines export job
 * POST /plans/{planId}/versions/{versionId}/lines/exports
 */
export async function createLineExport(
  planId: string,
  versionId: string,
  request?: CreateLineExportRequest
): Promise<CreateExportResponse> {
  const response = await api.post<CreateExportResponse>(
    `/plans/${planId}/versions/${versionId}/lines/exports`,
    request ?? {}
  )
  return response.data
}

/**
 * Get plan lines export status and download URL
 * GET /plans/{planId}/versions/{versionId}/lines/exports/{exportId}
 */
export async function getLineExport(
  planId: string,
  versionId: string,
  exportId: string
): Promise<ExportRead> {
  const response = await api.get<ExportRead>(
    `/plans/${planId}/versions/${versionId}/lines/exports/${exportId}`
  )
  return response.data
}
